import React, { useState, useRef, useCallback } from 'react';
import {
  Upload, Sparkles, FileImage, FileText, X,
  Trash2, Check, AlertCircle, Loader2, RotateCcw, Zap,
  Package
} from 'lucide-react';
import { host } from '../script/variables';
import { uploadAndGetKeyV2 } from '../script/uploadViaPresignV2';
import '../style/InventoryScan.css';



/* ─── Constants ─────────────────────────────────────────── */
const INVENTORY_CATEGORIES = [
  'vegetables',
  'dairy',
  'grains_and_cereals',
  'spices_and_herbs',
  'oils_and_fats',
  'meat_and_poultry',
  'seafood',
  'beverages',
  'fruits',
  'bakery',
  'condiments_and_sauces',
  'dry_goods',
  'other',
];

const CATEGORY_LABELS = {
  vegetables:           'Vegetables',
  dairy:                'Dairy Products',
  grains_and_cereals:   'Grains & Cereals',
  spices_and_herbs:     'Spices & Herbs',
  oils_and_fats:        'Oils & Fats',
  meat_and_poultry:     'Meat & Poultry',
  seafood:              'Seafood',
  beverages:            'Beverages',
  fruits:               'Fruits',
  bakery:               'Bakery & Baking',
  condiments_and_sauces:'Condiments & Sauces',
  dry_goods:            'Dry Goods',
  other:                'Other',
};

const CATEGORY_ICONS = {
  vegetables:           '🥦',
  dairy:                '🥛',
  grains_and_cereals:   '🌾',
  spices_and_herbs:     '🌿',
  oils_and_fats:        '🫙',
  meat_and_poultry:     '🍗',
  seafood:              '🐟',
  beverages:            '🧃',
  fruits:               '🍋',
  bakery:               '🍞',
  condiments_and_sauces:'🫙',
  dry_goods:            '📦',
  other:                '🗂️',
};

const UNITS = ['kg', 'g', 'liter', 'ml', 'pcs'];

const normaliseItem = (raw) => ({
  _key:             Math.random().toString(36).slice(2),
  name:             raw.name             || '',
  category:         INVENTORY_CATEGORIES.includes(raw.category) ? raw.category : 'other',
  unit:             UNITS.includes(raw.unit) ? raw.unit : 'kg',
  quantity:         raw.quantity         ?? 0,
  unitPrice:        raw.unitPrice        ?? 0,
  baseUnit: UNITS.includes(raw.baseUnit) ? raw.baseUnit : raw.unit || 'kg',
  baseQuantityPerUnit: raw.baseQuantityPerUnit ?? 1,
  lowStockThreshold: Math.max(0, Math.floor((raw.quantity ?? 0) * 0.2)),
});

/* ═══════════════════════════════════════════════════════════
   API HELPERS  (update URLs to match your backend)
═══════════════════════════════════════════════════════════ */
const extractItems = async (token, cafeid, fileKey) => {
  const r = await fetch(`${host}/api/inventory/invoieAiExtraction`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body:    JSON.stringify({ cafeid, fileKey }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data?.msg || `Extraction failed (${r.status})`);
  const items = Array.isArray(data.inventory) ? data.inventory : [];
  const invoiceId = data.invoiceId;


  if (items.length === 0) throw new Error('No items found. Try a clearer image.');
  return { items, invoiceId };
};

/* Strip _key before sending */
const bulkAddItems = async (token, cafeid, items,invoiceId) => {
  const clean = items.map(({ _key, ...item }) => item);
  const r = await fetch(`${host}/api/inventory/bulkAdd`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body:    JSON.stringify({ cafeid, inventory: clean,invoiceId  }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data?.msg || 'Bulk save failed');
  return data;
};

const addDailyExpense = async (token, cafeid, totalValue,invoiceId) => {
  const res = await fetch(`${host}/api/expense/daily`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body:    JSON.stringify({ cafeId:cafeid, note:"Inventoy Cost (AI Scanned)",amount:totalValue  }),
  });
  const j       = await res.json();
  if (!res.ok) throw new Error(j.msg || 'Failed to save under daily expense');
  return j;
};

/* ═══════════════════════════════════════════════════════════
   ITEM ROW
═══════════════════════════════════════════════════════════ */
const ItemRow = ({ item, rowNum, onUpdate, onRemove }) => {
  const upd = (field, val) => onUpdate(item._key, { [field]: val });

  const totalPrice = (item.quantity * item.unitPrice).toFixed(2);

  return (
    <tr className="is-row">
      <td className="is-td-idx">{rowNum}</td>

      {/* Name */}
      <td className="is-td is-td-first">
        <input
          className="is-inp"
          value={item.name}
          placeholder="Item name…"
          onChange={e => upd('name', e.target.value)}
        />
      </td>

      {/* Category */}
      <td className="is-td">
        <select
          className="is-select"
          value={item.category}
          onChange={e => upd('category', e.target.value)}
        >
          {INVENTORY_CATEGORIES.map(c => (
            <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
          ))}
        </select>
      </td>

      {/* Quantity */}
      <td className="is-td is-td-num">
        <input
          className="is-inp is-inp-num"
          type="number"
          min="0"
          value={item.quantity || ''}
          placeholder="—"
          onChange={e => upd('quantity', parseFloat(e.target.value) || 0)}
        />
      </td>

      {/* Unit */}
      <td className="is-td">
        <select
          className="is-select is-select-unit"
          value={item.unit}
        //   onChange={e => upd('unit', e.target.value)}
          onChange={e => {
            const newUnit = e.target.value;
            upd('unit', newUnit);

            // auto sync
            if (newUnit !== 'pcs') {
                upd('baseUnit', newUnit);
                upd('baseQuantityPerUnit', 1);
            }
            }}
        >
          {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      </td>
      {/* Base Unit */}
        <td className="is-td">
        <select
            className="is-select is-select-unit"
            value={item.baseUnit || item.unit}
            onChange={e => upd('baseUnit', e.target.value)}
        >
            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        </td>

        {/* Base Quantity */}
        <td className="is-td is-td-num">
        <input
            className="is-inp is-inp-num"
            type="number"
            min="0.0001"
            step="0.01"
            value={item.baseQuantityPerUnit || ''}
            placeholder="1"
            onChange={e =>
            upd('baseQuantityPerUnit', parseFloat(e.target.value) || 1)
            }
        />
        </td>

      {/* Unit Price */}
      <td className="is-td is-td-num">
        <div className="is-price-wrap">
          <span className="is-rupee">₹</span>
          <input
            className="is-inp is-inp-num"
            type="number"
            min="0"
            value={item.unitPrice || ''}
            placeholder="—"
            onChange={e => upd('unitPrice', parseFloat(e.target.value) || 0)}
          />
        </div>
      </td>
      <td className="is-td is-td-num">
        <input
            className="is-inp is-inp-num"
            type="number"
            min="0"
            value={item.lowStockThreshold ?? 0}
            placeholder="0"
            onChange={e =>
            upd('lowStockThreshold', parseFloat(e.target.value) || 0)
            }
        />
        </td>

      {/* Total (read-only derived) */}
      <td className="is-td is-td-total">
        <span className="is-total-val">₹{totalPrice}</span>
      </td>

      {/* Delete */}
      <td className="is-td-del">
        <button className="is-del-btn" onClick={() => onRemove(item._key)} title="Remove">
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN: InventoryScan
═══════════════════════════════════════════════════════════ */
const InventoryScan = ({ cafeid }) => {
  const token = localStorage.getItem('partnertoken');

  const [phase,      setPhase]      = useState('upload');
  const [file,       setFile]       = useState(null);
  const [preview,    setPreview]    = useState(null);
  const [uploadPct,  setUploadPct]  = useState(0);
  const [items,      setItems]      = useState([]);
  const [error,      setError]      = useState('');
  const [dragging,   setDragging]   = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const inputRef = useRef();

  const total      = items.length;
  const totalValue = items.reduce((sum, i) => sum + (i.quantity * i.unitPrice), 0);

  /* Group items by category for the review table */
  const grouped = INVENTORY_CATEGORIES
    .map(cat => ({ cat, rows: items.filter(i => i.category === cat) }))
    .filter(g => g.rows.length > 0);

  const acceptFile = useCallback((f) => {
    if (!f) return;
    if (!['image/jpeg','image/png','image/webp','image/heic','application/pdf'].includes(f.type)) {
      setError('Please upload a JPG, PNG, WEBP or PDF file.'); return;
    }
    if (f.size > 20 * 1024 * 1024) {
      setError('File too large. Max 20 MB.'); return;
    }
    setFile(f);
    setError('');
    setPreview(f.type !== 'application/pdf' ? URL.createObjectURL(f) : 'pdf');
  }, []);

  const onDrop      = useCallback((e) => { e.preventDefault(); setDragging(false); acceptFile(e.dataTransfer.files[0]); }, [acceptFile]);
  const onDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  const handleExtract = async () => {
    if (!file) return;
    setError('');
    try {
      setPhase('uploading'); setUploadPct(0);
      const { fileKey } = await uploadAndGetKeyV2(file, 'inventory', setUploadPct, cafeid);
      setPhase('extracting');
      const { items, invoiceId } = await extractItems(token, cafeid, fileKey);
      setInvoiceId(invoiceId);
      setItems(items.map(normaliseItem));
      setPhase('review');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please retry.');
      setPhase('upload');
    }
  };

  const removeItem = (_key) => setItems(prev => prev.filter(i => i._key !== _key));

  const updateItem = (_key, updated) =>
    setItems(prev => prev.map(i => i._key === _key ? { ...i, ...updated } : i));

  const handleBulkImport = async () => {
    if (!total) return;
    setBulkSaving(true);
    setError('');
    try {
      await bulkAddItems(token, cafeid, items,invoiceId);
       try {
        await addDailyExpense(token, cafeid,totalValue,invoiceId);
      } catch (expenseErr) {
        console.error("Expense failed", expenseErr);
        setError("Items saved, but expense entry failed.");
      }
      setPhase('done');
    } catch (err) {
      setError(err.message || 'Import failed. Please retry.');
    } finally {
      setBulkSaving(false);
    }
  };



  const reset = () => {
    setPhase('upload'); setFile(null); setPreview(null);
    setItems([]); setError(''); setUploadPct(0); setBulkSaving(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  /* ══════════════════════════════════════════════════════════
     RENDER: UPLOAD
  ══════════════════════════════════════════════════════════ */
  if (phase === 'upload') return (
    <div className="is-root">
      <div className="is-upload-wrap">
        <div className="is-badge"><Sparkles size={11} /><span>AI-Powered</span></div>
        <h2 className="is-hero-title">InventoryScan</h2>
        <p className="is-hero-sub">Upload an invoice or bill — every item extracted automatically.</p>

        <div
          className={`is-dropzone ${dragging ? 'drag' : ''} ${file ? 'ready' : ''}`}
          onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
          onClick={() => !file && inputRef.current?.click()}
        >
          <input ref={inputRef} type="file" accept="image/*,.pdf"
            style={{ display: 'none' }}
            onChange={e => acceptFile(e.target.files[0])} />

          {!file ? (
            <>
              <div className="is-dz-icon"><Upload size={22} /></div>
              <p className="is-dz-main">Drop your invoice here</p>
              <p className="is-dz-hint">JPG · PNG · WEBP · PDF &nbsp;·&nbsp; max 20 MB</p>
              <div className="is-dz-pills">
                <span className="is-dz-pill"><FileImage size={10} /> Photo</span>
                <span className="is-dz-pill"><FileText size={10} /> PDF Invoice</span>
                <span className="is-dz-pill"><FileImage size={10} /> Receipt</span>
              </div>
            </>
          ) : (
            <>
              {preview !== 'pdf'
                ? <img src={preview} alt="Invoice preview" className="is-preview-img" />
                : <div className="is-dz-icon" style={{ margin: '0 auto 12px' }}><FileText size={22} /></div>
              }
              <div className="is-file-meta">
                <span className="is-file-name">{file.name}</span>
                <span className="is-file-size">{(file.size / 1024).toFixed(0)} KB</span>
              </div>
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <button className="is-file-clear" onClick={e => { e.stopPropagation(); reset(); }}>
                  <X size={11} /> Change file
                </button>
              </div>
            </>
          )}
        </div>

        {error && <div className="is-error-box"><AlertCircle size={14} /><span>{error}</span></div>}

        <button className="is-extract-btn" onClick={handleExtract} disabled={!file}>
          <Sparkles size={15} /> Extract Items from Invoice
        </button>

        <div className="is-tips">
          <p className="is-tips-title">Tips for best results</p>
          <ul>
            <li>Ensure item names, quantities and prices are clearly visible</li>
            <li>PDFs work great — even scanned ones</li>
            <li>Works with Hindi / regional language invoices too</li>
            <li>Multiple pages? Merge into one PDF for best results</li>
          </ul>
        </div>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     RENDER: UPLOADING
  ══════════════════════════════════════════════════════════ */
  if (phase === 'uploading') return (
    <div className="is-root is-center">
      <div className="is-progress-card">
        <div className="is-phase-icon"><Upload size={22} /></div>
        <h3 className="is-phase-title">Uploading invoice…</h3>
        <div className="is-prog-bar-wrap">
          <div className="is-prog-bar" style={{ width: `${uploadPct}%` }} />
        </div>
        <p className="is-prog-label">{uploadPct}%</p>
        <p className="is-phase-hint">Sending to secure cloud storage</p>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     RENDER: EXTRACTING
  ══════════════════════════════════════════════════════════ */
  if (phase === 'extracting') return (
    <div className="is-root is-center">
      <div className="is-progress-card">
        {preview && preview !== 'pdf' ? (
          <div className="is-scan-wrap">
            <div className="is-scan-line" />
            <img src={preview} alt="" className="is-scan-img" />
          </div>
        ) : (
          <div className="is-phase-icon"><FileText size={22} /></div>
        )}
        <div className="is-extract-status">
          <Loader2 size={15} className="is-spin" style={{ color: '#4f8ef7' }} />
          <span>AI is reading your invoice…</span>
        </div>
        <p className="is-phase-hint">Identifying items, quantities and prices</p>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     RENDER: REVIEW TABLE
  ══════════════════════════════════════════════════════════ */
  if (phase === 'review') return (
    <div className="is-root is-review-shell">

      <div className="is-review-header">
        <div>
          <div className="is-review-title-row">
            <h2 className="is-review-title">Review Items</h2>
            <span className="is-count-badge">{total}</span>
          </div>
          <p className="is-review-sub">Review and edit, then import everything in one click.</p>
        </div>
        <div className="is-header-actions">
          <button className="is-ghost-btn" onClick={reset}><RotateCcw size={12} /> Start Over</button>
          <button className="is-import-btn" onClick={handleBulkImport} disabled={bulkSaving || !total}>
            {bulkSaving
              ? <><Loader2 size={13} className="is-spin" /> Saving…</>
              : <><Zap size={13} /> Import {total} Items</>
            }
          </button>
        </div>
      </div>

      {error && (
        <div className="is-error-box" style={{ margin: '12px 28px 0' }}>
          <AlertCircle size={14} /><span>{error}</span>
        </div>
      )}

      {/* ── Summary bar ── */}
      <div className="is-summary-bar">
        <span className="is-summary-label">
          <Package size={13} /> <strong>{total}</strong> items &nbsp;·&nbsp; Est. total: <strong>₹{totalValue.toFixed(2)}</strong>
        </span>
      </div>

      <div className="is-table-wrap">
        <table className="is-table">
          <thead>
            <tr>
              <th className="is-th" style={{ width: 36 }}>#</th>
              <th className="is-th">Item Name</th>
              <th className="is-th">Category</th>
              <th className="is-th is-th-num">Qty</th>
              <th className="is-th">Unit</th>
              <th className="is-th">Base Unit</th>
              <th className="is-th is-th-num">Base Quantity</th>
              <th className="is-th is-th-num">Unit Price</th>
              <th className="is-th is-th-num">Low Stock</th>
              <th className="is-th is-th-num">Total</th>
              <th className="is-th" style={{ width: 44 }}></th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              let rowNum = 0;
              return grouped.map(({ cat, rows }) => [
                /* Category header row */
                <tr key={`cat-${cat}`}>
                  <td colSpan={11} className="is-td-cat-header">
                    <span className="is-cat-icon">{CATEGORY_ICONS[cat]}</span>
                    {CATEGORY_LABELS[cat]}
                    <span className="is-cat-count">{rows.length}</span>
                  </td>
                </tr>,
                /* Item rows */
                ...rows.map((item) => {
                  rowNum += 1;
                  return (
                    <ItemRow
                      key={item._key}
                      item={item}
                      rowNum={rowNum}
                      onUpdate={updateItem}
                      onRemove={removeItem}
                    />
                  );
                }),
              ]);
            })()}
          </tbody>
        </table>
      </div>

      <div className="is-footer">
        <div className="is-footer-stats">
          <span className="is-stat-chip">{total} items</span>
          <span className="is-stat-chip is-stat-chip--value">₹{totalValue.toFixed(2)} total</span>
        </div>
        <button className="is-import-btn" onClick={handleBulkImport} disabled={bulkSaving || !total}>
          {bulkSaving
            ? <><Loader2 size={13} className="is-spin" /> Saving all items…</>
            : <><Zap size={13} /> Import All {total} Items</>
          }
        </button>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     RENDER: DONE
  ══════════════════════════════════════════════════════════ */
  if (phase === 'done') return (
    <div className="is-root is-center">
      <div className="is-done-card">
        <div className="is-done-icon"><Check size={28} /></div>
        <h3 className="is-done-title">Import Complete!</h3>
        <p className="is-done-count">{total} items added to your inventory</p>
        <button className="is-rescan-btn" onClick={reset}>
          <RotateCcw size={13} /> Scan Another Invoice
        </button>
      </div>
    </div>
  );

  return null;
};

export default InventoryScan;